import { useState, useEffect } from 'react'
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  query,
  orderBy 
} from 'firebase/firestore'
import { db } from '@lib/firebase'

export const useFirestore = (collectionName, { subscribe = true } = {}) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Comprehensive Total Rewards Services - Complete coverage of all 10 consulting domains + 61 benefit categories
  const defaultServices = [
    {
      id: 'equity',
      title: 'Equity Compensation & Long-Term Incentives',
      subtitle: 'Comprehensive equity programs and long-term incentive strategies',
      description: 'End-to-end equity compensation solutions including stock options, RSUs, ESOPs, LTIs, and equity incentive plans designed to align employee interests with company growth and retention.',
      subServices: [
        {
          name: 'Equity Plan Design & Strategy',
          description: 'RSU vs Stock Options education, plan design based on company goals, global equity strategy (tax, legal, vesting)'
        },
        {
          name: 'Equity Allocation & Pool Management',
          description: 'Equity pool modeling & allocation tiers, cap table impact simulation with scenario planning, equity pool sizing'
        },
        {
          name: 'Grant Practice & Vesting Policy',
          description: 'Grant frequency, timing, cliff setup, customized vesting linked to performance/milestones'
        },
        {
          name: 'Long-Term Incentive (LTI) Design',
          description: 'LTI modeling aligned to growth, custom LTI plans with performance metrics, strategic linking with business planning'
        },
        {
          name: 'Employee Education & Communication',
          description: 'Equity 101 sessions, live training & FAQs, personalized education + equity portals'
        },
        {
          name: 'Regulatory & Compliance Support',
          description: 'Local tax & securities compliance, cross-border compliance mapping, 409A valuations'
        },
        {
          name: 'Talent Retention Modeling',
          description: 'Predictive attrition analysis, LTI modeling for high-risk roles, retention strategies'
        }
      ],
      features: [
        'Stock Option & RSU Plan Design',
        'ESOP Strategy & Implementation',
        'Long-Term Incentive Programs',
        'Equity Pool Sizing & Allocation',
        'Cap Table Impact Analysis',
        'Vesting Schedule Optimization',
        'Cross-Border Compliance',
        'Employee Equity Education',
        'Retention Modeling & Analytics'
      ],
      benefits: [
        'Attract and retain top talent with competitive equity packages',
        'Align employee interests with long-term company growth',
        'Ensure compliance with regulatory requirements globally',
        'Optimize tax implications and cap table management',
        'Drive performance through strategic long-term incentives'
      ],
      process: [
        'Comprehensive equity audit and gap analysis',
        'Strategic equity plan design and LTI modeling',
        'Legal documentation and compliance setup',
        'Implementation with employee education programs',
        'Ongoing management, optimization, and retention analytics'
      ],
      color: 'from-primary-500 to-primary-600',
      bgColor: 'from-primary-50 to-primary-100'
    },
    {
      id: 'executive',
      title: 'Executive Compensation & IPO Readiness',
      subtitle: 'Strategic C-suite pay programs and IPO preparation consulting',
      description: 'Comprehensive executive compensation strategies including C-suite pay design, IPO readiness, governance compliance, and shareholder alignment for sustainable leadership rewards.',
      subServices: [
        {
          name: 'Executive Pay Philosophy & Strategy',
          description: 'Developing comprehensive pay philosophies aligned with business strategy, global compensation strategy design'
        },
        {
          name: 'C-Suite Compensation Design',
          description: 'Structuring pay packages for CXOs including base salary, bonuses, stock options, perquisites, and succession planning'
        },
        {
          name: 'Executive Benchmarking & Market Analysis',
          description: 'Proxy comp benchmarking, peer group analysis, market leader alignment & pay mix modeling'
        },
        {
          name: 'IPO Readiness & Due Diligence',
          description: 'Pre-IPO total rewards audit, CD&A preparation, equity disclosure strategy, IPO-readiness audit'
        },
        {
          name: 'Governance & Compliance Management',
          description: 'Board advisory, say-on-pay preparation, SOX compliance, internal controls for compensation'
        },
        {
          name: 'Performance & Retention Planning',
          description: 'Lock-in bonus structures, retention design aligned to IPO milestones, performance metric alignment'
        }
      ],
      features: [
        'C-Suite Pay Package Design',
        'Executive Benchmarking & Analytics',
        'IPO Readiness & Due Diligence',
        'Board Advisory & Governance',
        'Say-on-Pay Preparation',
        'CD&A Development & Disclosure',
        'Executive Retention Programs',
        'SOX Compliance Management',
        'Performance Alignment Strategies'
      ],
      benefits: [
        'Align executive pay with business strategy and performance',
        'Ensure IPO readiness and regulatory compliance',
        'Meet governance and shareholder expectations',
        'Enhance market positioning and investor confidence',
        'Drive sustainable leadership performance'
      ],
      process: [
        'Executive compensation audit and IPO readiness assessment',
        'Strategic pay philosophy and governance framework development',
        'Market benchmarking and competitive positioning analysis',
        'Board presentation, approval process, and compliance setup',
        'Implementation with ongoing monitoring and optimization'
      ],
      color: 'from-primary-600 to-primary-700',
      bgColor: 'from-primary-50 to-primary-100'
    },
    {
      id: 'variable',
      title: 'Variable Pay & Sales Compensation',
      subtitle: 'Performance-driven incentive programs and sales compensation excellence',
      description: 'Comprehensive variable pay and sales compensation solutions including STI design, sales commission structures, performance metrics, and incentive optimization for maximum business impact.',
      subServices: [
        {
          name: 'Short-Term Incentive (STI) Design',
          description: 'Performance-linked variable plans, custom matrix based on KPIs & OKRs, weighted scorecard models'
        },
        {
          name: 'Sales Compensation Strategy',
          description: 'Sales role-specific benchmarks, quota setting by territory/segment, tiered plans with accelerators'
        },
        {
          name: 'Bonus Plan Design & Structure',
          description: 'General % targets by level, tiered incentive structures, advanced modeling with goal alignment'
        },
        {
          name: 'Performance Metrics & KPI Design',
          description: 'Basic KPI alignment, customized success metrics with automation, performance metric design'
        },
        {
          name: 'Sales Operations Enablement',
          description: 'CRM linkage guidance, plan effectiveness review, automation, dashboards, clawback modeling'
        },
        {
          name: 'Plan Communication & Training',
          description: 'Rolling out variable pay plans, manager toolkits, calculation and award method communication'
        }
      ],
      features: [
        'Short-Term Incentive (STI) Programs',
        'Sales Commission Structures',
        'Performance Bonus Frameworks',
        'KPI & OKR-Based Incentives',
        'Sales Quota & Territory Design',
        'Plan Effectiveness Analytics',
        'CRM Integration & Automation',
        'Clawback & Risk Management',
        'Manager Training & Toolkits'
      ],
      benefits: [
        'Drive performance and business results',
        'Motivate sales and non-sales teams effectively',
        'Align rewards with strategic business objectives',
        'Optimize sales productivity and revenue generation',
        'Ensure competitive and fair incentive packages'
      ],
      process: [
        'Current incentive program assessment and gap analysis',
        'STI and sales compensation strategy development',
        'Performance metrics design and KPI alignment',
        'Implementation with CRM integration and training',
        'Ongoing monitoring, optimization, and effectiveness review'
      ],
      color: 'from-primary-400 to-primary-500',
      bgColor: 'from-primary-50 to-primary-100'
    },
    {
      id: 'benefits',
      title: 'Benefits Strategy & Comprehensive Employee Benefits',
      subtitle: 'Complete benefits ecosystem design and optimization',
      description: 'End-to-end benefits strategy covering health, wellness, financial, mobility, flexibility, family support, learning, lifestyle, and recognition programs that create compelling employee value propositions.',
      subServices: [
        {
          name: 'Health, Insurance & Well-being Benefits',
          description: 'Medical insurance, dental & vision plans, mental health benefits, life insurance, critical illness cover, health check-ups, wellness allowances'
        },
        {
          name: 'Financial, Mobility & Asset-Based Benefits',
          description: 'Car lease policies, mobile phone allowances, WFH setup reimbursements, tech purchase policies, internet reimbursements, commuter support'
        },
        {
          name: 'Leave, Relocation & Flexibility Programs',
          description: 'PTO policies, parental leave (maternity/paternity), childcare support, relocation assistance, sabbatical leave, work from anywhere, hybrid work policies'
        },
        {
          name: 'Learning, Growth & Career Benefits',
          description: 'Tuition reimbursement, certification support, conference/workshop policies, career coaching programs, professional development'
        },
        {
          name: 'Lifestyle, Family & Pet-Friendly Policies',
          description: 'Pet adoption leave, child education support, festival vouchers, marriage/bereavement leave, emergency loans, employee gifting programs'
        },
        {
          name: 'Recognition, Discounts & Perks',
          description: 'Long service awards, employee discount programs, spot bonus policies, referral bonuses, meal subsidies, team offsites, peer recognition platforms'
        },
        {
          name: 'Benefits Cost Optimization & Analytics',
          description: 'Benefit utilization analytics, total cost modeling across regions, vendor selection & management, ROI optimization'
        }
      ],
      features: [
        'Comprehensive Health & Wellness Programs',
        'Financial & Mobility Benefits Design',
        'Flexible Work & Leave Policies',
        'Learning & Development Benefits',
        'Family-Friendly & Lifestyle Perks',
        'Recognition & Reward Programs',
        'Benefits Cost Optimization',
        'Vendor Management & Analytics',
        'Digital Benefits Platforms',
        'Global Benefits Harmonization'
      ],
      benefits: [
        'Create comprehensive employee well-being ecosystem',
        'Optimize benefits costs while maximizing employee satisfaction',
        'Ensure competitive positioning across all benefit categories',
        'Support diverse employee needs and life stages',
        'Drive engagement through innovative benefits design'
      ],
      process: [
        'Comprehensive benefits audit and employee needs assessment',
        'Strategic benefits design across all categories',
        'Vendor evaluation, selection, and cost optimization',
        'Implementation with employee communication and training',
        'Ongoing analytics, optimization, and ROI measurement'
      ],
      color: 'from-primary-400 to-primary-600',
      bgColor: 'from-primary-50 to-primary-100'
    },
    {
      id: 'benchmarking',
      title: 'Compensation Consulting & Market Benchmarking',
      subtitle: 'Strategic compensation design with data-driven market intelligence',
      description: 'Comprehensive compensation consulting including salary structure design, pay philosophy development, job evaluation, pay equity audits, and advanced market benchmarking for competitive positioning.',
      subServices: [
        {
          name: 'Benchmarking & Market Analysis',
          description: 'Custom surveys & proprietary market analysis, formal salary benchmarking using external databases, pay data insights'
        },
        {
          name: 'Salary Structure & Job Architecture Design',
          description: 'Role architecture with geo-based pay design, grade & band structure with midpoint strategy, structured job leveling frameworks'
        },
        {
          name: 'Pay Philosophy & Strategy Development',
          description: 'Global compensation strategy design, aligning philosophy with business goals, drafting comprehensive pay principles'
        },
        {
          name: 'Job Evaluation & Levelling',
          description: 'Proprietary point-factor or bespoke job evaluation, structured job leveling frameworks, role classification systems'
        },
        {
          name: 'Pay Equity & Fair Pay Audits',
          description: 'DEI-linked compensation audits, internal pay equity analysis, systematic equity reviews across teams'
        },
        {
          name: 'Merit Increase & Performance Pay Strategy',
          description: 'Performance-based merit matrix, multi-scenario forecasting tools, pay for performance differentiation models'
        },
        {
          name: 'Compensation Communication & Training',
          description: 'Strategic communication plans with executive buy-in, manager toolkits, personalized comp letters & messaging'
        }
      ],
      features: [
        'Custom Market Surveys & Analysis',
        'Salary Structure & Job Architecture',
        'Pay Philosophy Development',
        'Job Evaluation & Leveling Systems',
        'Pay Equity Audits & Remediation',
        'Merit Increase Matrix Design',
        'Performance Pay Strategies',
        'Manager Training & Toolkits',
        'Compensation Communication Plans'
      ],
      benefits: [
        'Make informed compensation decisions with reliable market data',
        'Ensure competitive and equitable pay structures',
        'Align compensation strategy with business objectives',
        'Optimize compensation budgets and ROI',
        'Drive performance through strategic pay design'
      ],
      process: [
        'Market data collection and competitive analysis',
        'Salary structure and job architecture design',
        'Pay philosophy development and strategic alignment',
        'Implementation with manager training and communication',
        'Ongoing monitoring, optimization, and equity analysis'
      ],
      color: 'from-primary-600 to-primary-800',
      bgColor: 'from-primary-50 to-primary-100'
    },
    {
      id: 'rewards',
      title: 'Total Rewards, Performance Management & Merit Systems',
      subtitle: 'Integrated total rewards with performance appraisal and merit increase excellence',
      description: 'Comprehensive total rewards programs combined with performance management systems, performance appraisal design, merit increase strategies, goal setting, feedback culture, and integrated reward strategies that drive organizational performance.',
      subServices: [
        {
          name: 'Total Rewards Philosophy & Integration',
          description: 'Culture-first reward design with storytelling, linking rewards to EVP, developing integrated total rewards strategies'
        },
        {
          name: 'Performance Appraisal System Design',
          description: 'EVP-linked high-performance culture design, continuous performance models (CFR, OKRs), 360° feedback processes, rating system design, calibration processes'
        },
        {
          name: 'Merit Increase Strategy & Matrix Design',
          description: 'Performance-based merit matrix, multi-scenario forecasting tools, pay for performance differentiation models, strategic timing aligned to budget & goals'
        },
        {
          name: 'Goal Setting & Performance Frameworks',
          description: 'OKR-based system integration, company-wide cascading goal frameworks, customized success metrics with automation, SMART goals introduction'
        },
        {
          name: 'Manager Training & Feedback Culture',
          description: 'Leadership alignment & coaching capability programs, training on unbiased reviews, feedback delivery, decision support tools, psychometric tools'
        },
        {
          name: 'Performance Review Technology & Forms',
          description: 'Platform-based integration with HRMS/ATS, dynamic customizable forms, real-time recommendation engines, talent council setup'
        },
        {
          name: 'Rewards Technology & Analytics',
          description: 'Total rewards tech stack design, predictive analytics & AI-based modeling, compensation communication strategy'
        },
        {
          name: 'Global Mobility & M&A Integration',
          description: 'End-to-end mobility reward modeling, white-glove post-merger reward harmonization, global rewards strategy'
        },
        {
          name: 'Employee Experience & Communication',
          description: 'Engagement campaigns with storytelling and videos, personalized total rewards statements, personalized comp letters & messaging'
        }
      ],
      features: [
        'Total Rewards Strategy & Philosophy',
        'Performance Appraisal System Design',
        'Merit Increase Matrix & Strategy',
        'OKR & Goal Setting Frameworks',
        'Manager Training & Feedback Culture',
        'Performance Review Technology',
        'Calibration & Talent Councils',
        'Rewards Technology & Analytics',
        'Global Mobility & M&A Support',
        'Employee Experience & Communication',
        'Predictive Analytics & AI Modeling'
      ],
      benefits: [
        'Create compelling and integrated employee value propositions',
        'Drive performance through aligned rewards and recognition',
        'Implement fair and transparent performance appraisal systems',
        'Optimize merit increase budgets and pay-for-performance strategies',
        'Support talent attraction, retention, and engagement',
        'Enable data-driven total rewards and performance decision making',
        'Ensure seamless global and M&A rewards integration'
      ],
      process: [
        'Total rewards, performance, and merit assessment',
        'Integrated strategy development and appraisal system design',
        'Merit matrix design and performance framework implementation',
        'Manager training and feedback culture development',
        'Technology implementation and employee communication',
        'Ongoing analytics, optimization, and performance measurement'
      ],
      color: 'from-primary-500 to-primary-700',
      bgColor: 'from-primary-50 to-primary-100'
    }
  ]

  // Initialize default data if collection is empty
  const initializeDefaultData = async () => {
    try {
      if (collectionName === 'services') {
        const servicesRef = collection(db, 'services')
        const snapshot = await getDocs(servicesRef)
        
        if (snapshot.empty) {
          console.log('Initializing default services data...')
          for (const service of defaultServices) {
            await setDoc(doc(db, 'services', service.id), service)
          }
          console.log('Default services data initialized successfully')
        }
      }
    } catch (error) {
      console.error('Error initializing default data:', error)
    }
  }

  useEffect(() => {
    if (!collectionName) return

    // Write-only callers must not open a read listener. `enquiries` and
    // `newsletterSubscribers` are admin-read-only by design, so subscribing
    // from the public contact form or the site footer raises
    // permission-denied on every page load — the write still succeeds, but the
    // console fills with errors and the hook parks in an error state.
    if (!subscribe) {
      setLoading(false)
      return
    }

    const collectionRef = collection(db, collectionName)
    const q = query(collectionRef, orderBy('id'))

    const unsubscribe = onSnapshot(q, 
      async (snapshot) => {
        try {
          if (snapshot.empty && collectionName === 'services') {
            await initializeDefaultData()
            return
          }

          const items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          
          setData(items)
          setError(null)
        } catch (err) {
          console.error('Error fetching data:', err)
          setError(err.message)
        } finally {
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error in snapshot listener:', err)
        setError(err.message)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [collectionName, subscribe])

  const addItem = async (item) => {
    try {
      // Use provided ID if it exists, otherwise generate new one
      const docId = item.id || doc(collection(db, collectionName)).id
      const docRef = doc(db, collectionName, docId)
      await setDoc(docRef, { ...item, id: docId })
      return docId
    } catch (error) {
      console.error('Error adding item:', error)
      throw error
    }
  }

  const updateItem = async (id, updates) => {
    try {
      const docRef = doc(db, collectionName, id)
      await updateDoc(docRef, updates)
    } catch (error) {
      console.error('Error updating item:', error)
      throw error
    }
  }

  const deleteItem = async (id) => {
    try {
      await deleteDoc(doc(db, collectionName, id))
    } catch (error) {
      console.error('Error deleting item:', error)
      throw error
    }
  }

  return {
    data,
    loading,
    error,
    addItem,
    updateItem,
    deleteItem,
    initializeDefaultData
  }
} 